<template>
<Container>
	<header class="minr-header m-b-4">
		<div class="custom-header">
			<a class="logo" :href="homepageUrl" :title="$t('clean energy fund dashboard')">
				<BGFLogo />
			</a>
			<div class="header-title"><h1>{{ $t("Ambition North Wales") }}</h1></div>
			<div class="lang-switch-wrapper">
				<div class="language-switch-wrapper">
					<div class="switch2">
						<input
							id="language-toggle"
							:checked="isEnglish"
							class="check-toggle check-toggle-round-flat"
							type="checkbox"
							role="switch"
							:aria-checked="isEnglish"
							@change="toggleLanguage"
							/>
						<label for="language-toggle">
							<span class="sr-only">{{ $t('Switch language, currently') }} {{ isEnglish ? $t('English') : $t('Welsh') }}</span>
						</label>
						<span class="on" aria-hidden="true">ENG</span>
						<span class="off" aria-hidden="true">CY</span>
					</div>
					<span class="sr-only" role="status" aria-live="polite">
						{{ isEnglish ? $t('Current language: English') : $t('Current language: Welsh') }}
					</span>
				</div>
			</div>
		<div class="header_wave"><svg preserveAspectRatio="none" viewBox="0 0 1200 120" xmlns="http://www.w3.org/2000/svg" style="fill: rgb(18, 57, 77); width: 280%; height: 100px;"><path d="M321.39 56.44c58-10.79 114.16-30.13 172-41.86 82.39-16.72 168.19-17.73 250.45-.39C823.78 31 906.67 72 985.66 92.83c70.05 18.48 146.53 26.09 214.34 3V0H0v27.35a600.21 600.21 0 00321.39 29.09z"></path></svg></div>
		</div>
	</header>
	<Nav v-if="isAuthenticated && routeRequiresAuth" @btnClick="logoutAndRedirect" />
	<main>
		<router-view />
	</main>
	<Footer class="flex wrap-mob space-between justify-center align-center">
		<template #poweredBy>
			<div class="legal-links flex wrap justify-center align-center justify-start-dt">
				<p class="color-white">{{ $t("Copyright") }}&copy; {{ new Date().getFullYear() }}</p>
				<a href="https://www.ambitionnorthwales.fund/privacy" target="_blank" rel="nofollow noopener">{{ $t("Privacy Policy") }}</a>
				<router-link :to="{name: 'faq'}" target="_blank">{{ $t("FAQ's") }}</router-link>
				<small>V{{ version }}</small>
			</div>
		</template>
		<UMiLogo />
	</Footer>
	<content-overlay v-if="getInterstitial.isLoading">
		<template #content>
			<Interstitial v-if="getInterstitial.isLoading" :loading-message="getInterstitial.message" />
		</template>
	</content-overlay>
	<div id="modals" />
	<notifications />
</Container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import LogoBar from '@/components/LogoBar';
import UMiLogo from '@/components/Icons/UMiLogo';
import BGFLogo from './components/Icons/BGFLogo';

export default {
	components: {
		LogoBar,
		UMiLogo,
		BGFLogo
	},
	provide () {
		return {
			stageOneSteps: this.stageOneSteps,
			stageTwoSteps: this.stageTwoSteps
		};
	},
	data () {
		return {
			version: process.env.VUE_APP_VERSION,

			stageOneSteps: [
				{ dynamicsId: 'DeclarationsEOI', pathName: 'DeclarationsEOI', name: 'Declarations' },
				{ dynamicsId: 'BusinessDetailsEOI', pathName: 'BusinessDetailsEOI', name: 'Business Details' },
				{ dynamicsId: 'ContactDetailsEOI', pathName: 'ContactDetailsEOI', name: 'Contact Details' },
				{ dynamicsId: 'LoanPurpose', pathName: 'LoanPurpose', name: 'Loan Purpose' }
			],
			stageTwoSteps: [
				{ dynamicsId: 'CreditSafeDeclaration', pathName: 'CreditSafeDeclaration', name: 'CreditSafe Declaration' },
				{ dynamicsId: 'BusinessFinances', pathName: 'BusinessFinances', name: 'Business Finances' },
				{ dynamicsId: 'CompanyOverview', pathName: 'CompanyOverview', name: 'Company Overview' },
				{ dynamicsId: 'DirectorsInformation', pathName: 'DirectorsInformation', name: 'Directors Information' },
				{ dynamicsId: 'ProfessionalAdvisers', pathName: 'ProfessionalAdvisers', name: 'Professional Advisers' },
				{ dynamicsId: 'ProjectDetails', pathName: 'ProjectDetails', name: 'Project Details' },
				{ dynamicsId: 'SupportingDocuments', pathName: 'SupportingDocuments', name: 'Supporting Documents' }
			],
			logos: [
				{
					url: '/North_of_Tyne_Combined_Authority_logo.png',
					width: 125,
					height: 125
				},
				{
					url: '/Sunderland-City-Council.png',
					width: 200,
					height: 200
				},
				{
					url: '/Gateshead-Council-Logo-1.png',
					width: 200,
					height: 200
				},
				{
					url: '/funded_by.png',
					width: 200,
					height: 200
				},
				{
					url: '/umi.png',
					width: 85,
					height: 64
				}
			]
		};
	},
	computed: {
		routeRequiresAuth () {
			return this.$route.meta.requiresAuth;
		},
		homepageUrl() {
			return this.getExtensionBroker
				? `${process.env.VUE_APP_RELATIVE_URL}broker-dashboard`
				: `${process.env.VUE_APP_RELATIVE_URL}dashboard`;
		},
		...mapGetters('auth', ['isAuthenticated']),
		...mapGetters('ui', ['getInterstitial']),
		isEnglish() {
			return this.$i18n.locale === 'en';
		},

		...mapGetters('auth', ['getExtensionBroker'])
	},
	watch: {
		'$i18n.locale'() {
			this.updateLogoutText();
		}
	},
	mounted () {
		// prepare sectors for business details in both EOI and application
		this.setParentSectors();
		this.setSectors();
	},
	methods: {
		logoutAndRedirect () {
			this.logout();
			this.$router.push({ name: 'Login' });
		},
		...mapActions('auth', ['logout']),
		...mapActions('settings', ['setParentSectors', 'setSectors']),
		toggleLanguage() {
			this.$i18n.locale = this.isEnglish ? 'cy' : 'en';
			this.updateLogoutText();
			// Safely set lang on <html>
			if (typeof document !== 'undefined') {
				document.documentElement.lang = this.$i18n.locale;
			}
		},
		updateLogoutText() {
			const buttons = document.querySelectorAll('.minr-btn.secondary-hollow');
			buttons.forEach((btn) => {
				if (btn.textContent.trim() === 'Log out' || 'Allgofnodi') {
					btn.textContent = this.$t('Logout');
				}
			});
			const fileupload = document.querySelectorAll('.minr-field input[type=file]');
			
 		}
	}
};
</script>
<style scoped>
.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>